// Builds this week — Robert's calendar in one card (Ship 3, Aug 26 2026).
//
// `build_scheduled_date` is a DATE column, so the range is a bare ISO string
// compare — never `new Date()` on a date-only value (the May 9 off-by-one).
// Completed builds stay on the list with a ✓ so the day reads as "done", not
// "empty"; a scheduled build that is still open on a past day is called out in
// amber because that is the one Robert has to chase.
//
// Dead deals are excluded, but `closed_won` is deliberately KEPT — a finished
// build legitimately walks into closing, and dropping it would blank out
// yesterday's completed work.
//
// Oct 26, 2026: a build can run more than one day (build_end_date). It shows
// on each of its days with "day 1 of 2", counts once in the totals, and is
// only "not marked" once its LAST day has passed.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../lib/supabase";
import { logCardClick } from "../../../lib/uiEvents";
import { addDaysISO, buildCovers, buildDayLabel, normalizeBuildEnd, BUILD_SPAN_LOOKBACK_DAYS } from "../../../lib/buildCalendar";

const DEAD = [
  "no_thanks",
  "insufficient_damage",
  "claims_denied",
  "cancelled",
  "closed_lost",
  "closed_inspection",
];
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const pad = (n: number) => String(n).padStart(2, "0");
const isoDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

interface BuildRow {
  id: string;
  homeowner_name: string | null;
  property_street: string | null;
  build_scheduled_date: string | null;
  build_end_date: string | null;
  build_completed_date: string | null;
}

/** A build's last day (its first day when it runs one day). */
const lastDayOf = (r: BuildRow) => (r.build_scheduled_date ? normalizeBuildEnd(r.build_scheduled_date, r.build_end_date) ?? r.build_scheduled_date : "");

export default function BuildsWeekWidget() {
  const [rows, setRows] = useState<BuildRow[] | null>(null);
  const [nextWeek, setNextWeek] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);

  const { weekISOs, todayISO } = useMemo(() => {
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    return {
      weekISOs: Array.from({ length: 7 }, (_, i) => {
        const d = new Date(monday);
        d.setDate(monday.getDate() + i);
        return isoDate(d);
      }),
      todayISO: isoDate(now),
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const monISO = weekISOs[0];
      const sunISO = weekISOs[6];
      const nextMon = new Date(`${sunISO}T12:00:00Z`);
      nextMon.setUTCDate(nextMon.getUTCDate() + 1);
      const nextSun = new Date(nextMon);
      nextSun.setUTCDate(nextMon.getUTCDate() + 6);
      const nextMonISO = nextMon.toISOString().slice(0, 10);
      const nextSunISO = nextSun.toISOString().slice(0, 10);

      const [wk, nx] = await Promise.all([
        supabase
          .from("deals")
          .select("id,homeowner_name,property_street,build_scheduled_date,build_end_date,build_completed_date")
          .gte("build_scheduled_date", addDaysISO(monISO, -BUILD_SPAN_LOOKBACK_DAYS))
          .lte("build_scheduled_date", sunISO)
          .not("stage", "in", `(${DEAD.join(",")})`)
          .order("build_scheduled_date", { ascending: true }),
        supabase
          .from("deals")
          .select("id", { count: "exact", head: true })
          .gte("build_scheduled_date", nextMonISO)
          .lte("build_scheduled_date", nextSunISO)
          .not("stage", "in", `(${DEAD.join(",")})`),
      ]);
      if (cancelled) return;
      if (wk.error) {
        setFailed(true);
        return;
      }
      // Keep builds that run on at least one day of this week.
      setRows(((wk.data as BuildRow[]) ?? []).filter((r) => lastDayOf(r) >= monISO));
      setNextWeek(nx.error ? null : nx.count ?? 0);
    })();
    return () => {
      cancelled = true;
    };
  }, [weekISOs]);

  const byDay = useMemo(() => {
    if (!rows) return null;
    return weekISOs.map((iso, i) => ({
      iso,
      dow: DOW[i],
      isToday: iso === todayISO,
      isPast: iso < todayISO,
      items: rows.filter((r) => buildCovers(r.build_scheduled_date, r.build_end_date, iso)),
    }));
  }, [rows, weekISOs, todayISO]);

  const done = rows?.filter((r) => !!r.build_completed_date).length ?? 0;
  const openLate = rows?.filter((r) => !r.build_completed_date && lastDayOf(r) < todayISO).length ?? 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-1">
        <span>🗓</span>
        <span className="font-bold text-sm text-bardin-ink">Builds this week</span>
        <span className="ml-auto text-xs font-bold text-slate-500">
          {failed ? "—" : rows ? `${done}/${rows.length} done` : "…"}
        </span>
      </div>

      {failed ? (
        <div className="text-xs text-slate-400 py-2">Couldn't load the build schedule — try a refresh.</div>
      ) : !byDay ? (
        <div className="space-y-2 mt-1">
          <div className="h-7 rounded-lg bg-slate-100 animate-pulse" />
          <div className="h-7 rounded-lg bg-slate-100 animate-pulse" />
        </div>
      ) : rows!.length === 0 ? (
        <div className="text-xs text-slate-400 py-2">Nothing on the build calendar this week.</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {byDay
            .filter((d) => d.items.length > 0 || d.isToday)
            .map((d) => (
              <div key={d.iso} className={`py-1.5 ${d.isToday ? "-mx-1 px-1 rounded-lg bg-amber-50/60" : ""}`}>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-[10px] font-extrabold uppercase tracking-wide w-9 ${
                      d.isToday ? "text-bardin-orange" : "text-slate-400"
                    }`}
                  >
                    {d.dow}
                  </span>
                  <span className="text-[10px] font-bold text-slate-400">
                    {d.items.length === 0 ? "no builds" : `${d.items.length} build${d.items.length === 1 ? "" : "s"}`}
                  </span>
                </div>
                {d.items.map((r) => (
                  <Link
                    key={r.id}
                    to={`/deal/${r.id}`}
                    onClick={() => logCardClick("widget_buildsweek_row")}
                    className="flex items-center gap-2 pl-9 py-0.5"
                  >
                    <span className="text-[13px] font-bold text-bardin-ink truncate">
                      {r.homeowner_name ?? "—"}
                    </span>
                    <span className="text-[11px] text-slate-400 truncate flex-1">
                      {r.property_street ?? ""}
                    </span>
                    {buildDayLabel(r.build_scheduled_date, r.build_end_date, d.iso) && (
                      <span className="text-[10px] font-bold text-slate-400 shrink-0">
                        {buildDayLabel(r.build_scheduled_date, r.build_end_date, d.iso).toLowerCase()}
                      </span>
                    )}
                    {r.build_completed_date ? (
                      <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-extrabold">
                        ✓ done
                      </span>
                    ) : d.isPast && lastDayOf(r) < todayISO ? (
                      <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-extrabold">
                        not marked
                      </span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] font-extrabold">
                        open
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            ))}
        </div>
      )}

      {openLate > 0 && (
        <div className="text-[10px] text-amber-700 font-semibold mt-1.5">
          ⚠ {openLate} build{openLate === 1 ? "" : "s"} earlier this week still isn't marked complete.
        </div>
      )}

      <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100">
        {nextWeek != null && (
          <span className="text-[10.5px] text-slate-500 font-semibold">
            {nextWeek} scheduled next week
          </span>
        )}
        <Link
          to="/dispatch?view=robert&tab=unscheduled"
          onClick={() => logCardClick("widget_buildsweek_open")}
          className="ml-auto text-[11px] font-bold text-bardin-orange"
        >
          Schedule board ›
        </Link>
      </div>
    </div>
  );
}
